import IconDiscountPercentage from '@/icons/IconDiscountPercentage.vue';
import type { GlobalProductPill, ThemeData } from '@/features/Page/data/models';
import type { MediaImage } from '@/components/models';
import type { ProductBadge } from '@/features/Product/types/models';
import { defaultTheme } from '@/features/Page/data/themes/defaultTheme';
import { truFitNavImageBlock } from '@/features/Page/data/themes/truFitTheme';

export const freeGiftBadgeImage: MediaImage = {
  src: 'https://cdn.shopify.com/s/files/1/0916/4078/files/Collection_Badge_-_Free_Gift_BF_26-_Single_da23463d-43af-4879-8733-7099f95194d3.png?v=1793057158',
  width: 300,
  height: 300,
  alt: 'Free gift',
};

const freeGiftCardBadge: ProductBadge = {
  position: 'bottom-right',
  media: freeGiftBadgeImage,
  desktopInset: '1rem',
};

const freeGiftCartPill: GlobalProductPill = {
  text: 'While stock lasts!',
  variant: 'custom',
  customVariantOptions: {
    backgroundColor: 'rgba(142, 142, 147, 1)',
    textColor: 'rgba(255, 255, 255, 1)',
  },
};

const truFitNavImageBlocks = {
  imageBlocks: [defaultTheme.navImageBlocks.default.imageBlocks[0], truFitNavImageBlock],
};

export const bfFreeGiftTheme: ThemeData = {
  ...defaultTheme,
  marqueeData: {
    default: {
      text: '<strong>FREE Gift</strong> with the Value Supply Bundle! Get in Quick 🎁',
      to: '/collections/value-supply-bundle',
      backgroundColour: 'rgba(216, 0, 39, 1)',
    },
  },
  navImageBlocks: {
    ...defaultTheme.navImageBlocks,
    au: truFitNavImageBlocks,
    us: truFitNavImageBlocks,
    uk: truFitNavImageBlocks,
  },
  saleNavLinks: {
    default: [
      {
        title: 'Free Gifts',
        link: '/collections/value-supply-bundle',
        sale: true,
        saleColour: '216, 0, 39',
        icon: IconDiscountPercentage,
      },
    ],
  },
  homeCampaignHero: {
    default: {
      overlay: true,
      backgroundColour: 'rgba(14, 15, 13, 1)',
      media: {
        desktop: {
          src: 'https://cdn.shopify.com/s/files/1/0916/4078/files/Page_Feature_-_BFFree_Gift_26_-_D_-_AU_US_UK.png?v=1792992483',
          width: 2880,
          height: 1410,
          alt: 'Value Supply Bundle products around a free gift box',
        },
        mobile: {
          src: 'https://cdn.shopify.com/s/files/1/0916/4078/files/Pop_Up_-_BF_Free_Gift_26_-_M_-_AU_US_UK.png?v=1792992483',
          width: 1170,
          height: 1620,
          alt: 'Value Supply Bundle products around a free gift box',
        },
      },
      pill: {
        text: "DON'T MISS YOUR FREE GIFT",
        variant: 'custom',
        customVariantOptions: {
          backgroundColor: 'rgba(228, 0, 43, 1)',
          textColor: 'rgba(255, 255, 255, 1)',
        },
      },
      title: 'SAVE BIG AND STOCK UP ON OUR BEST SELLERS WITH THE VALUE SUPPLY BUNDLE',
      description: 'And select a <strong>FREE</strong> gift from our most loved products.',
      button: {
        text: 'Explore Value Supply Bundles',
        to: '/collections/value-supply-bundle',
      },
    },
  },
  productCardBadgesBySku: {
    default: {
      '10069-TW15': freeGiftCardBadge,
      TOOTH_ARMOUR_BFGS: freeGiftCardBadge,
      '10068-PO8': freeGiftCardBadge,
      '10060-VS5': freeGiftCardBadge,
      '50001-TF-WH5': freeGiftCardBadge,
    },
  },
  collectionsNavAdditions: {
    default: [{ url: '/collections/value-supply-bundle', name: 'Value Supply Bundle' }],
  },
  collectionBannersByHandle: {
    default: {
      'value-supply-bundle': {
        banner: {
          desktop: {
            src: 'https://cdn.shopify.com/s/files/1/0916/4078/files/Sale_Page_Banner_-_BF_Free_Gift_26_-_D_-_AU_US_UK_6d5f7863-538c-4b69-9052-019ad8acf39e.png?v=1793319473',
            width: 3870,
            height: 900,
            alt: 'Free gift with every Value Supply Bundle',
          },
          mobile: {
            src: 'https://cdn.shopify.com/s/files/1/0916/4078/files/Collection_Banner_-_YSB_free_gift_-_BF26_-_M.png?v=1793319464',
            width: 1242,
            height: 720,
            alt: 'Free gift with every Value Supply Bundle',
          },
        },
        hideText: true,
        backgroundColour: 'rgba(52, 25, 26, 1)',
      },
    },
  },
  freeGiftProduct: {
    default: {
      requiresEligibleItem: true,
      options: [
        { displaySku: '10069-TW', bundleItemSku: '10069-TW', cartSku: 'FREE_VSB_GIFT' },
        { displaySku: '50001-TF-WH', bundleItemSku: '50001-TF-WH', cartSku: 'FREE_VSB_GIFT' },
        { displaySku: '10060-VS', bundleItemSku: '10060-VS', cartSku: 'FREE_VSB_GIFT' },
        { displaySku: '10065-TA', bundleItemSku: '10065-TA', cartSku: 'FREE_VSB_GIFT' },
        { displaySku: '10068-PO', bundleItemSku: '10068-PO', cartSku: 'FREE_VSB_GIFT' },
      ],
      secondGiftCartSku: 'FREE_VSB_GIFT',
      chooserBannerImage: {
        src: 'https://cdn.shopify.com/s/files/1/0916/4078/files/Free_gift_Selector_Banner_-_BF_Free_gift_26_-_MD_-_AU_US_UK.png?v=1792992484',
        width: 2358,
        height: 549,
        alt: 'Free gift',
      },
      cartPill: freeGiftCartPill,
      secondGiftCartPill: { ...freeGiftCartPill, text: 'Email only free gift' },
      cartBannerImage: {
        src: 'https://cdn.shopify.com/s/files/1/0916/4078/files/Cart_Badge_-_Free_Gift_BF_26_-_M_b75b90f2-6f5b-4d96-808f-97352da72309.png?v=1793165620',
        width: 558,
        height: 135,
        alt: 'Free gift',
      },
      eligibleAtcButtonText: 'Add to cart + pick free gift',
      optionRowBadge: freeGiftBadgeImage,
    },
  },
};
